'use client';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import { isEmpty, isString } from '@wconcept/wconcept-common/utils/baseUtils';
import React, { forwardRef, memo, useCallback } from 'react';

import { sanitizeContent } from '@wconcept/wconcept-common/utils/htmlSanitizer';
import * as S from './style';

interface IProps {
  /** className */
  className?: string;
  /** children */
  children: React.ReactNode;
  /** size */
  size?: string | number;
  /** weight */
  weight?: string | number;
  /** line-height */
  lineHeight?: string | number;
  /** letter-spacing */
  letterSpacing?: string | number;
  /** color */
  color?: string;
  /** align */
  align?: string; // 'left' | 'center' | 'right'
  /** max-width */
  maxWidth?: string | number;
  /** block style */
  block?: boolean;
  /** paragraph style */
  paragraph?: boolean;
  /** underline style */
  underline?: boolean;
  /** del style */
  del?: boolean;
  /** strong style */
  strong?: boolean;
  /** em style */
  em?: boolean;
  /** italic style */
  italic?: boolean;
  /** ellipsis */
  ellipsis?: number;
}

const Text = forwardRef<HTMLElement, IProps>(
  (
    {
      className,
      children,
      size,
      weight,
      lineHeight,
      letterSpacing,
      color,
      align,
      maxWidth,
      block = false,
      paragraph = false,
      underline = false,
      del = false,
      strong = false,
      em = false,
      italic = false,
      ellipsis = 0,
    },
    ref
  ) => {
    let as = 'span';
    if (block) {
      as = 'div';
    } else if (paragraph) {
      as = 'p';
    }
    const param = {
      className: classNameBind(['text', className || '']),
      $size: size,
      $weight: weight,
      $color: color,
      $align: align,
      $lineHeight: lineHeight,
      $letterSpacing: letterSpacing,
      $underline: underline,
      $del: del,
      $strong: strong,
      $em: em,
      $italic: italic,
      $ellipsis: ellipsis,
      as,
      $maxWidth: maxWidth,
    };
    let childrenProp = children;

    if (!underline && del) childrenProp = <del>{children}</del>;
    if (italic) childrenProp = <i>{children}</i>;
    if (em) childrenProp = <em>{children}</em>;
    if (strong) childrenProp = <strong>{children}</strong>;

    const str = (node: React.ReactNode) => String(node);

    return (
      <>
        {!isEmpty(childrenProp) && isString(childrenProp) ? (
          <S.Text
            ref={ref}
            {...param}
            dangerouslySetInnerHTML={{
              __html: sanitizeContent(str(childrenProp)),
            }}
          />
        ) : (
          <S.Text ref={ref} {...param}>
            {childrenProp}
          </S.Text>
        )}
      </>
    );
  }
);

Text.displayName = 'Text';
export default Text;
